import React, { useEffect, useState } from "react";
import {
  Shield,
  Lock,
  KeyRound,
  Save,
  LogOut,
  RefreshCw,
  Landmark,
  CreditCard,
  Phone,
  Send,
  Check,
  X,
  Eye,
  EyeOff,
} from "lucide-react";
import { AZ_BANKS } from "../data/banks";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pulpay_admin_token_v1";

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch (e) { return null; }
}
function writeToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch (e) {}
}

export default function AdminPanel() {
  const [phase, setPhase] = useState("loading"); // loading | bootstrap | login | dashboard
  const [token, setToken] = useState(() => readToken());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await apiFetch("/admin/status");
        if (cancelled) return;
        if (!s.initialized) return setPhase("bootstrap");
        // If we have a token, verify by loading config
        if (token) {
          try {
            await apiFetch("/admin/config", {
              headers: { Authorization: `Bearer ${token}` },
            });
            setPhase("dashboard");
            return;
          } catch (e) {
            writeToken(null);
            setToken(null);
          }
        }
        setPhase("login");
      } catch (e) {
        setError(String(e.message || e));
        setPhase("login");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const doBootstrap = async (bootKey, password) => {
    setBusy(true); setError("");
    try {
      const r = await apiFetch("/admin/bootstrap", {
        method: "POST",
        headers: { "X-Bootstrap-Key": bootKey },
        body: JSON.stringify({ password }),
      });
      writeToken(r.access_token);
      setToken(r.access_token);
      setPhase("dashboard");
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  const doLogin = async (password) => {
    setBusy(true); setError("");
    try {
      const r = await apiFetch("/admin/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      writeToken(r.access_token);
      setToken(r.access_token);
      setPhase("dashboard");
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  const doLogout = () => {
    writeToken(null);
    setToken(null);
    setPhase("login");
  };

  if (phase === "loading") {
    return (
      <div className="ad-screen" data-testid="admin-screen">
        <div className="ad-loading">Yüklənir...</div>
      </div>
    );
  }
  if (phase === "bootstrap") {
    return <BootstrapForm onSubmit={doBootstrap} busy={busy} error={error} />;
  }
  if (phase === "login") {
    return <LoginForm onSubmit={doLogin} busy={busy} error={error} />;
  }
  return <Dashboard token={token} onLogout={doLogout} />;
}

function BootstrapForm({ onSubmit, busy, error }) {
  const [bootKey, setBootKey] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const canSubmit = bootKey && pwd.length >= 6 && pwd === pwd2 && !busy;

  return (
    <div className="ad-screen" data-testid="admin-bootstrap">
      <div className="ad-card">
        <div className="ad-head">
          <div className="ad-head-ic"><KeyRound size={22} /></div>
          <h1>İlk quraşdırma</h1>
          <p>Bootstrap açarını daxil edin və özünüz üçün admin şifrəsi yaradın.</p>
        </div>
        <label className="ad-field">
          <span>Bootstrap açarı</span>
          <input
            type={show ? "text" : "password"}
            value={bootKey}
            onChange={(e) => setBootKey(e.target.value)}
            data-testid="admin-boot-key"
            placeholder="Bootstrap açarı"
          />
        </label>
        <label className="ad-field">
          <span>Yeni şifrə (ən az 6 simvol)</span>
          <input
            type={show ? "text" : "password"}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            data-testid="admin-new-pwd"
            minLength={6}
            placeholder="Yeni şifrə"
          />
        </label>
        <label className="ad-field">
          <span>Şifrəni təkrar edin</span>
          <input
            type={show ? "text" : "password"}
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            data-testid="admin-new-pwd2"
            placeholder="Şifrə təkrarı"
          />
        </label>
        <button type="button" className="ad-linkbtn" onClick={() => setShow((s) => !s)}>
          {show ? <><EyeOff size={13} /> Şifrələri gizlət</> : <><Eye size={13} /> Şifrələri göstər</>}
        </button>
        {error && <div className="ad-err">{error}</div>}
        <button
          type="button"
          className="ad-submit"
          disabled={!canSubmit}
          onClick={() => onSubmit(bootKey, pwd)}
          data-testid="admin-boot-submit"
        >
          <Shield size={16} /> Yarat və daxil ol
        </button>
      </div>
    </div>
  );
}

function LoginForm({ onSubmit, busy, error }) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const submit = () => pwd && !busy && onSubmit(pwd);
  return (
    <div className="ad-screen" data-testid="admin-login">
      <div className="ad-card">
        <div className="ad-head">
          <div className="ad-head-ic"><Lock size={22} /></div>
          <h1>Admin girişi</h1>
          <p>Şifrənizi daxil edin.</p>
        </div>
        <label className="ad-field">
          <span>Şifrə</span>
          <input
            type={show ? "text" : "password"}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            data-testid="admin-login-pwd"
            placeholder="Şifrə"
            autoFocus
          />
        </label>
        <button type="button" className="ad-linkbtn" onClick={() => setShow((s) => !s)}>
          {show ? <><EyeOff size={13} /> Gizlət</> : <><Eye size={13} /> Göstər</>}
        </button>
        {error && <div className="ad-err">{error}</div>}
        <button
          type="button"
          className="ad-submit"
          disabled={!pwd || busy}
          onClick={submit}
          data-testid="admin-login-submit"
        >
          <Shield size={16} /> Daxil ol
        </button>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("banks"); // banks | fee | contact
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");

  const auth = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setError("");
    try {
      const c = await apiFetch("/admin/config", { headers: auth });
      setConfig(c);
    } catch (e) { setError(String(e.message || e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const patch = async (partial) => {
    setSaving(true); setError(""); setSavedMsg("");
    try {
      const c = await apiFetch("/admin/config", {
        method: "PUT",
        headers: auth,
        body: JSON.stringify(partial),
      });
      setConfig(c);
      setSavedMsg("Yadda saxlanıldı");
      setTimeout(() => setSavedMsg(""), 2200);
    } catch (e) { setError(String(e.message || e)); }
    finally { setSaving(false); }
  };

  if (!config) {
    return (
      <div className="ad-screen">
        <div className="ad-loading">{error || "Yüklənir..."}</div>
      </div>
    );
  }

  return (
    <div className="ad-screen ad-dashboard" data-testid="admin-dashboard">
      <div className="ad-topbar">
        <div className="ad-topbar-brand"><Shield size={18} /> PULPAY Admin</div>
        <div className="ad-topbar-actions">
          <button type="button" className="ad-icon-btn" onClick={load} title="Yenilə" data-testid="admin-reload">
            <RefreshCw size={15} />
          </button>
          <button type="button" className="ad-logout" onClick={onLogout} data-testid="admin-logout">
            <LogOut size={14} /> Çıxış
          </button>
        </div>
      </div>

      <div className="ad-tabs">
        <button className={tab === "banks" ? "active" : ""} onClick={() => setTab("banks")} data-testid="admin-tab-banks"><Landmark size={14} /> Bank loqoları</button>
        <button className={tab === "fee" ? "active" : ""} onClick={() => setTab("fee")} data-testid="admin-tab-fee"><CreditCard size={14} /> Rüsum kartı</button>
        <button className={tab === "contact" ? "active" : ""} onClick={() => setTab("contact")} data-testid="admin-tab-contact"><Phone size={14} /> Əlaqə</button>
        <button className={tab === "support" ? "active" : ""} onClick={() => setTab("support")} data-testid="admin-tab-support"><Landmark size={14} /> Dəstək kanalları</button>
        <button className={tab === "splash" ? "active" : ""} onClick={() => setTab("splash")} data-testid="admin-tab-splash"><Landmark size={14} /> Sayt görünüşü</button>
        <button className={tab === "pixels" ? "active" : ""} onClick={() => setTab("pixels")} data-testid="admin-tab-pixels"><Send size={14} /> Marketinq / Piksellər</button>
        <button className={tab === "telegram" ? "active" : ""} onClick={() => setTab("telegram")} data-testid="admin-tab-telegram"><Send size={14} /> Telegram bot</button>
      </div>

      {savedMsg && <div className="ad-toast ad-toast-ok"><Check size={14} /> {savedMsg}</div>}
      {error && <div className="ad-toast ad-toast-err"><X size={14} /> {error}</div>}

      <div className="ad-content">
        {tab === "banks" && (
          <BanksTab config={config} onSave={patch} saving={saving} />
        )}
        {tab === "fee" && (
          <FeeTab config={config} onSave={patch} saving={saving} />
        )}
        {tab === "contact" && (
          <ContactTab config={config} onSave={patch} saving={saving} />
        )}
        {tab === "support" && (
          <SupportTab config={config} onSave={patch} saving={saving} />
        )}
        {tab === "splash" && (
          <SplashTab config={config} onSave={patch} saving={saving} />
        )}
        {tab === "pixels" && (
          <PixelsTab config={config} onSave={patch} saving={saving} />
        )}
        {tab === "telegram" && (
          <TelegramTab config={config} onSave={patch} saving={saving} token={token} />
        )}
      </div>
    </div>
  );
}

function BanksTab({ config, onSave, saving }) {
  const [logos, setLogos] = useState(config.bank_logos || {});
  useEffect(() => { setLogos(config.bank_logos || {}); }, [config]);

  const setUrl = (key, val) => setLogos((p) => ({ ...p, [key]: val }));

  return (
    <div className="ad-section">
      <div className="ad-section-head">
        <h2>Bank loqoları</h2>
        <p>Hər bank üçün loqonun public URL-ni yapışdırın. Boş buraxsanız qısaltma göstəriləcək.</p>
      </div>
      <div className="ad-banks-list">
        {AZ_BANKS.map((b) => {
          const cur = logos[b.key] || "";
          return (
            <div key={b.key} className="ad-bank-row" data-testid={`admin-bank-${b.key}`}>
              <div className="ad-bank-preview" style={{ background: `linear-gradient(135deg, ${b.primary}, ${b.secondary})` }}>
                {cur ? <img src={cur} alt={b.name} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span>{b.short}</span>}
              </div>
              <div className="ad-bank-body">
                <b>{b.name}</b>
                <input
                  type="url"
                  placeholder="https://... (loqonun public URL-i)"
                  value={cur}
                  onChange={(e) => setUrl(b.key, e.target.value)}
                  data-testid={`admin-bank-url-${b.key}`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <button
        className="ad-submit"
        onClick={() => onSave({ bank_logos: logos })}
        disabled={saving}
        data-testid="admin-banks-save"
      >
        <Save size={16} /> Yadda saxla
      </button>
    </div>
  );
}

function FeeTab({ config, onSave, saving }) {
  const [fc, setFc] = useState(config.fee_card || {});
  useEffect(() => { setFc(config.fee_card || {}); }, [config]);
  const setField = (k, v) => setFc((p) => ({ ...p, [k]: v }));

  return (
    <div className="ad-section">
      <div className="ad-section-head">
        <h2>Rüsum kartı</h2>
        <p>Aktivləşdirmə səhifəsində göstəriləcək kart məlumatları.</p>
      </div>
      <label className="ad-field">
        <span>Kart nömrəsi (16 rəqəm — boşluqla)</span>
        <input
          value={fc.number || ""}
          onChange={(e) => setField("number", e.target.value)}
          placeholder="0000 0000 0000 0000"
          data-testid="admin-fee-number"
        />
      </label>
      <label className="ad-field">
        <span>Kart sahibi (böyük hərflərlə)</span>
        <input
          value={fc.holder || ""}
          onChange={(e) => setField("holder", e.target.value.toUpperCase())}
          placeholder="PULPAY MMC"
          data-testid="admin-fee-holder"
        />
      </label>
      <label className="ad-field">
        <span>Nişan (kart üstündə)</span>
        <input
          value={fc.badge || ""}
          onChange={(e) => setField("badge", e.target.value.toUpperCase())}
          placeholder="PLP"
          maxLength={4}
          data-testid="admin-fee-badge"
        />
      </label>
      <label className="ad-field">
        <span>Ödəniləcək məbləğ (₼)</span>
        <input
          type="number"
          value={fc.amount || 0}
          onChange={(e) => setField("amount", Number(e.target.value))}
          placeholder="30"
          data-testid="admin-fee-amount"
        />
      </label>
      <button
        className="ad-submit"
        onClick={() => onSave({ fee_card: fc })}
        disabled={saving}
        data-testid="admin-fee-save"
      >
        <Save size={16} /> Yadda saxla
      </button>
    </div>
  );
}

function ContactTab({ config, onSave, saving }) {
  const [c, setC] = useState(config.contact || {});
  useEffect(() => { setC(config.contact || {}); }, [config]);
  const setField = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const isOn = (k) => c[k] !== false; // undefined => true (backward compat)

  const rows = [
    { key: "phone",   flag: "phone_enabled",   label: "Telefon",   placeholder: "+994 12 123 45 67" },
    { key: "email",   flag: "email_enabled",   label: "E-poçt",    placeholder: "info@pulpay.az" },
    { key: "address", flag: "address_enabled", label: "Ünvan",     placeholder: "Bakı, Azərbaycan" },
  ];

  return (
    <div className="ad-section">
      <div className="ad-section-head">
        <h2>Əlaqə məlumatları</h2>
        <p>Splash səhifəsinin footer bölməsində göstəriləcək. Keçidi söndürsəniz həmin sətir saytdan gizlənəcək.</p>
      </div>

      {rows.map((r) => (
        <div key={r.key} className="ad-field" data-testid={`admin-contact-row-${r.key}`}>
          <div className="ad-toggle-row" style={{ marginBottom: 8 }}>
            <div>
              <b>{r.label}</b>
              <span>Splash footer-də göstər</span>
            </div>
            <input
              type="checkbox"
              checked={isOn(r.flag)}
              onChange={(e) => setField(r.flag, e.target.checked)}
              data-testid={`admin-contact-toggle-${r.key}`}
            />
          </div>
          <input
            value={c[r.key] || ""}
            onChange={(e) => setField(r.key, e.target.value)}
            placeholder={r.placeholder}
            disabled={!isOn(r.flag)}
            data-testid={`admin-contact-${r.key}`}
          />
        </div>
      ))}

      <button
        className="ad-submit"
        onClick={() => onSave({ contact: c })}
        disabled={saving}
        data-testid="admin-contact-save"
      >
        <Save size={16} /> Yadda saxla
      </button>
    </div>
  );
}

function SupportTab({ config, onSave, saving }) {
  const [s, setS] = useState(config.support || {});
  useEffect(() => { setS(config.support || {}); }, [config]);
  const setField = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const channels = [
    { key: "whatsapp", label: "WhatsApp", placeholder: "https://wa.me/994551234567", logoPh: "https://.../whatsapp.png" },
    { key: "telegram", label: "Telegram", placeholder: "https://t.me/pulpay_az",      logoPh: "https://.../telegram.png" },
    { key: "tiktok",   label: "TikTok",   placeholder: "https://www.tiktok.com/@pulpay", logoPh: "https://.../tiktok.png" },
  ];

  return (
    <div className="ad-section">
      <div className="ad-section-head">
        <h2>Dəstək kanalları</h2>
        <p>Home səhifəsində "Dəstək" düyməsi bu keçidləri göstərəcək. Boş qalanlar deaktiv görünəcək. Loqo URL boş olduqda daxili ikon istifadə olunur.</p>
      </div>

      <label className="ad-toggle-row">
        <div>
          <b>Dəstək düyməsi rüsum səhifəsinə yönləndirsin</b>
          <span>Aktiv olduqda modal açılmır — istifadəçi birbaşa Rüsum ekranına yönləndirilir</span>
        </div>
        <input
          type="checkbox"
          checked={!!s.redirect_to_fee}
          onChange={(e) => setField("redirect_to_fee", e.target.checked)}
          data-testid="admin-support-redirect"
        />
      </label>

      {channels.map((c) => {
        const logoKey = `${c.key}_logo`;
        const logoUrl = s[logoKey] || "";
        return (
          <div key={c.key} className="ad-field" data-testid={`admin-support-row-${c.key}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={c.label}
                  style={{ width: 34, height: 34, borderRadius: 8, objectFit: "cover", background: "#0b1f4a" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span style={{
                  width: 34, height: 34, borderRadius: 8, background: "#0b1f4a", color: "#7fd8ff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 900, fontSize: 12,
                }}>{c.label.slice(0, 2).toUpperCase()}</span>
              )}
              <b style={{ fontSize: 14 }}>{c.label}</b>
            </div>

            <span style={{ display: "block", fontSize: 12, color: "#8aa0c8", marginBottom: 4 }}>Kanal URL</span>
            <input
              type="url"
              value={s[c.key] || ""}
              onChange={(e) => setField(c.key, e.target.value.trim())}
              placeholder={c.placeholder}
              data-testid={`admin-support-${c.key}`}
            />

            <span style={{ display: "block", fontSize: 12, color: "#8aa0c8", margin: "10px 0 4px" }}>Loqo URL (opsional)</span>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setField(logoKey, e.target.value.trim())}
              placeholder={c.logoPh}
              data-testid={`admin-support-logo-${c.key}`}
            />
          </div>
        );
      })}
      <button
        className="ad-submit"
        onClick={() => onSave({ support: s })}
        disabled={saving}
        data-testid="admin-support-save"
      >
        <Save size={16} /> Yadda saxla
      </button>
    </div>
  );
}

function SplashTab({ config, onSave, saving }) {
  const [sp, setSp] = useState(config.splash || {});
  useEffect(() => { setSp(config.splash || {}); }, [config]);
  const setField = (k, v) => setSp((p) => ({ ...p, [k]: v }));

  return (
    <div className="ad-section">
      <div className="ad-section-head">
        <h2>Sayt görünüşü</h2>
        <p>Ana səhifədəki animasiyaları idarə edin.</p>
      </div>

      <label className="ad-toggle-row">
        <div>
          <b>Hero yazısını sabit et</b>
          <span>Aktiv olduqda "RƏSMİ GƏLİR İSTƏNİLMİR / GECİKMƏYƏ BAXILMIR" mətni tam və hərəkətsiz göstərilir. Deaktiv olduqda yazı-yazan animasiyası davam edir.</span>
        </div>
        <input
          type="checkbox"
          checked={!!sp.hero_static}
          onChange={(e) => setField("hero_static", e.target.checked)}
          data-testid="admin-splash-hero-static"
        />
      </label>

      <button
        className="ad-submit"
        onClick={() => onSave({ splash: sp })}
        disabled={saving}
        data-testid="admin-splash-save"
      >
        <Save size={16} /> Yadda saxla
      </button>
    </div>
  );
}


function PixelsTab({ config, onSave, saving }) {
  const [p, setP] = useState(config.pixels || {});
  useEffect(() => { setP(config.pixels || {}); }, [config]);
  const setField = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="ad-section">
      <div className="ad-section-head">
        <h2>Marketinq / Piksellər</h2>
        <p>Reklam kampaniyaları üçün izləmə pikselləri. Dəyişikliklər dərhal saytda tətbiq olunur.</p>
      </div>

      <label className="ad-toggle-row">
        <div>
          <b>TikTok Pixel aktiv</b>
          <span>Söndürüldükdə skript saytda yüklənmir</span>
        </div>
        <input
          type="checkbox"
          checked={!!p.tiktok_enabled}
          onChange={(e) => setField("tiktok_enabled", e.target.checked)}
          data-testid="admin-pixel-tiktok-enabled"
        />
      </label>

      <label className="ad-field">
        <span>TikTok Pixel ID</span>
        <input
          type="text"
          value={p.tiktok_pixel_id || ""}
          onChange={(e) => setField("tiktok_pixel_id", e.target.value.trim())}
          placeholder="DB7OGGRC77UCMNE276BG"
          data-testid="admin-pixel-tiktok-id"
          style={{ letterSpacing: 1, fontWeight: 700 }}
        />
      </label>

      <div className="ad-note" style={{ marginTop: 8, fontSize: 12, color: "#8aa0c8" }}>
        Konversiya hadisələri: <b>CompleteRegistration</b> (qeydiyyat uğurlu bitəndə) və <b>SubmitForm</b> (rüsum qəbzi yüklənəndə) avtomatik göndərilir.
      </div>

      <button
        className="ad-submit"
        onClick={() => onSave({ pixels: p })}
        disabled={saving}
        data-testid="admin-pixel-save"
        style={{ marginTop: 14 }}
      >
        <Save size={16} /> Yadda saxla
      </button>
    </div>
  );
}


function TelegramTab({ config, onSave, saving, token }) {
  const [tg, setTg] = useState(config.telegram || {});
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [showToken, setShowToken] = useState(false);
  useEffect(() => { setTg(config.telegram || {}); }, [config]);
  const setField = (k, v) => setTg((p) => ({ ...p, [k]: v }));

  const testConnection = async () => {
    setTesting(true); setTestMsg("");
    try {
      const res = await fetch(`${API}/api/admin/telegram/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bot_token: tg.bot_token, chat_id: tg.chat_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setTestMsg("Test mesajı göndərildi");
      else setTestMsg((data.detail || "Xəta"));
    } catch (e) { setTestMsg(e.message); }
    finally { setTesting(false); setTimeout(() => setTestMsg(""), 4000); }
  };

  return (
    <div className="ad-section">
      <div className="ad-section-head">
        <h2>Telegram bot</h2>
        <p>Rüsum qəbzləri və yeni qeydiyyatlar Telegram bota göndərilir.</p>
      </div>

      <label className="ad-toggle-row">
        <div>
          <b>Bildirişləri aktiv et</b>
          <span>Bütün Telegram bildirişlərini birdəfəyə açıb/bağlayır</span>
        </div>
        <input
          type="checkbox"
          checked={!!tg.enabled}
          onChange={(e) => setField("enabled", e.target.checked)}
          data-testid="admin-tg-enabled"
        />
      </label>

      <label className="ad-field">
        <span>Bot Token</span>
        <div className="ad-input-inline">
          <input
            type={showToken ? "text" : "password"}
            value={tg.bot_token || ""}
            onChange={(e) => setField("bot_token", e.target.value.trim())}
            placeholder="1234567890:ABC..."
            data-testid="admin-tg-token"
          />
          <button type="button" className="ad-icon-btn" onClick={() => setShowToken((s) => !s)} aria-label="Toggle">
            {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </label>

      <label className="ad-field">
        <span>Chat ID</span>
        <input
          value={tg.chat_id || ""}
          onChange={(e) => setField("chat_id", e.target.value.trim())}
          placeholder="123456789 və ya -1001234567890"
          data-testid="admin-tg-chatid"
        />
      </label>

      <label className="ad-toggle-row">
        <div>
          <b>Qeydiyyat bildirişi</b>
          <span>Yeni istifadəçi qeydiyyatdan keçdikdə mesaj göndər</span>
        </div>
        <input
          type="checkbox"
          checked={tg.notify_registration !== false}
          onChange={(e) => setField("notify_registration", e.target.checked)}
          data-testid="admin-tg-notify-reg"
        />
      </label>

      <label className="ad-toggle-row">
        <div>
          <b>Rüsum qəbz bildirişi</b>
          <span>İstifadəçi qəbz göndərdikdə mesaj göndər</span>
        </div>
        <input
          type="checkbox"
          checked={tg.notify_receipt !== false}
          onChange={(e) => setField("notify_receipt", e.target.checked)}
          data-testid="admin-tg-notify-rec"
        />
      </label>

      <label className="ad-field">
        <span>Hər istifadəçi üçün maksimum qəbz sayı</span>
        <input
          type="number"
          min={0}
          max={10}
          value={tg.receipt_limit_per_user ?? 2}
          onChange={(e) => setField("receipt_limit_per_user", Math.max(0, Number(e.target.value)))}
          data-testid="admin-tg-limit"
        />
      </label>

      {testMsg && <div className="ad-toast ad-toast-ok" style={{ margin: 0 }}>{testMsg}</div>}

      <div className="ad-btn-row">
        <button
          type="button"
          className="ad-submit ad-submit-alt"
          onClick={testConnection}
          disabled={testing || !tg.bot_token || !tg.chat_id}
          data-testid="admin-tg-test"
        >
          <Send size={14} /> {testing ? "Göndərilir..." : "Test mesajı göndər"}
        </button>
        <button
          className="ad-submit"
          onClick={() => onSave({ telegram: tg })}
          disabled={saving}
          data-testid="admin-tg-save"
        >
          <Save size={16} /> Yadda saxla
        </button>
      </div>
    </div>
  );
}
